<script>
import WebLayout from '@/layouts/Web/Layout.vue'

export default {
    layout: WebLayout,
}
</script>
<script setup>
import GwIcon from '@/components/GwIcon.vue'
import GwNumeralGrad from '@/components/GwNumeralGrad.vue'
import { computed, ref } from 'vue'
import SEOHead from '@/components/SEOHead.vue'
import { useWebTranslations } from '@/composables/useWebTranslations.js'
import AllFeatures from '@/Pages/Web/components/AllFeatures.vue'

const { get, getArray, locale, webRoute, alternateLocales } = useWebTranslations()

const isYearly = ref(true)
const openFaq = ref(null)
const openTaxId = ref(null)

const packages = [
  {
    id: 'landing',
    labelKey: 'home.pricing_section.packages.landing.label',
    subtitleKey: 'home.pricing_section.packages.landing.subtitle',
    featuresKey: 'home.pricing_section.packages.landing.features',
    monthly: { exVat: 10, withVat: 12.20 },
    yearly: { exVatPerMonth: 7.99, withVatPerMonth: 9.75, withVatPerYear: 117.00 },
  },
  {
    id: 'ecommerce_basic',
    labelKey: 'home.pricing_section.packages.ecommerce_basic.label',
    subtitleKey: 'home.pricing_section.packages.ecommerce_basic.subtitle',
    featuresKey: 'home.pricing_section.packages.ecommerce_basic.features',
    monthly: { exVat: 12, withVat: 14.64 },
    yearly: { exVatPerMonth: 10, withVatPerMonth: 12.20, withVatPerYear: 146.40 },
  },
  {
    id: 'ecommerce_advanced',
    labelKey: 'home.pricing_section.packages.ecommerce_advanced.label',
    subtitleKey: 'home.pricing_section.packages.ecommerce_advanced.subtitle',
    featuresKey: 'home.pricing_section.packages.ecommerce_advanced.features',
    monthly: { exVat: 25, withVat: 30.50 },
    yearly: { exVatPerMonth: 20, withVatPerMonth: 24.40, withVatPerYear: 292.80 },
  },
]

const numberLocaleMap = {
  sl: 'sl-SI',
  en: 'en-GB',
  hr: 'hr-HR',
  sr: 'sr-RS',
  bs: 'bs-BA',
}
const numberLocale = computed(() => numberLocaleMap[locale.value] ?? 'en-GB')

function formatPrice(amount) {
  return new Intl.NumberFormat(numberLocale.value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

function displayPrice(pkg) {
  return isYearly.value ? pkg.yearly.exVatPerMonth : pkg.monthly.exVat
}

function yearlySavingsPercent(pkg) {
  const monthly = pkg.monthly.exVat
  if (!monthly) return 0
  return Math.round(((monthly - pkg.yearly.exVatPerMonth) / monthly) * 100)
}

function taxDetails(pkg) {
  if (isYearly.value) {
    return { monthly: pkg.yearly.withVatPerMonth, yearly: pkg.yearly.withVatPerYear }
  }
  return { monthly: pkg.monthly.withVat }
}

function toggleTax(id) {
  openTaxId.value = openTaxId.value === id ? null : id
}

const faqs = [
  { q: 'static.pricing.faq.items.0.q', a: 'static.pricing.faq.items.0.a' },
  { q: 'static.pricing.faq.items.1.q', a: 'static.pricing.faq.items.1.a' },
  { q: 'static.pricing.faq.items.2.q', a: 'static.pricing.faq.items.2.a' },
  { q: 'static.pricing.faq.items.3.q', a: 'static.pricing.faq.items.3.a' },
  { q: 'static.pricing.faq.items.4.q', a: 'static.pricing.faq.items.4.a' },
  { q: 'static.pricing.faq.items.5.q', a: 'static.pricing.faq.items.5.a' },
  { q: 'static.pricing.faq.items.6.q', a: 'static.pricing.faq.items.6.a' },
]

const contactUrl = computed(() => `${webRoute('web.home')}#contact`)
const seoTitle = computed(() => `${get('pricing.title')} · Growiyo`)
const seoDescription = computed(() => get('pricing.seo_description'))
const canonicalPath = computed(() => webRoute('web.pricing'))
const pageAlternateLocales = computed(() => alternateLocales('web.pricing'))
</script>

<template>
  <SEOHead
    :title="seoTitle"
    :description="seoDescription"
    :canonical="canonicalPath"
    :locale="locale"
    :alternate-locales="pageAlternateLocales"
    type="website"
  />

  <!-- ===================================================================
    01 · HERO — 3 package cards + monthly/yearly toggle
  ==================================================================== -->
  <section class="gw-paper">
    <div class="max-w-[1280px] mx-auto px-6 lg:px-12 pt-24 pb-20 lg:pt-36 lg:pb-28">

      <div class="text-center mb-12">
        <p class="gw-mono text-[11px] font-medium uppercase tracking-[0.28em] text-[rgba(51,51,51,0.5)] mb-6">
          {{ get('pricing.heading') }}
        </p>
        <h1 class="text-[1.5rem] lg:text-[1.85rem] font-light leading-[1.3] text-[color:var(--gw-ink)] max-w-2xl mx-auto tracking-[-0.018em]" style="font-family: 'Plus Jakarta Sans', sans-serif;">
          {{ get('static.pricing.hero.heading_prefix') }}
          <em class="italic font-extrabold" style="background: var(--gw-gradient); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; font-style: italic;">{{ get('static.pricing.hero.heading_emphasis') }}</em>.
        </h1>
      </div>

      <!-- Billing toggle -->
      <div class="flex justify-center mb-10">
        <div class="inline-flex items-center gap-1 p-1 rounded-full bg-white border gw-rule-strong">
          <button
            type="button"
            class="px-5 py-2 rounded-full text-[11px] gw-mono font-semibold uppercase tracking-[0.18em] transition-all"
            :class="isYearly ? 'bg-[color:var(--gw-ink)] text-white' : 'text-[rgba(51,51,51,0.55)] hover:text-[color:var(--gw-ink)]'"
            @click="isYearly = true"
          >{{ get('pricing.yearly') }}</button>
          <button
            type="button"
            class="px-5 py-2 rounded-full text-[11px] gw-mono font-semibold uppercase tracking-[0.18em] transition-all"
            :class="!isYearly ? 'bg-[color:var(--gw-ink)] text-white' : 'text-[rgba(51,51,51,0.55)] hover:text-[color:var(--gw-ink)]'"
            @click="isYearly = false"
          >{{ get('pricing.monthly') }}</button>
        </div>
      </div>

      <!-- 3-column package cards -->
      <ul class="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[rgba(51,51,51,0.10)] border gw-rule-strong list-none p-0 m-0">
        <li
          v-for="pkg in packages"
          :key="pkg.id"
          class="relative bg-white p-8 lg:p-9 flex flex-col"
        >
          <span
            v-if="isYearly"
            class="absolute top-4 right-4 z-10 gw-mono text-[10px] font-semibold uppercase tracking-[0.16em] px-2.5 py-1 text-white rotate-6"
            style="background: #16a34a;"
          >
            {{ get('home.pricing_section.save_prefix') }} {{ yearlySavingsPercent(pkg) }}%
          </span>

          <h3 class="text-[1.05rem] font-semibold text-[color:var(--gw-ink)] tracking-[-0.012em]">
            {{ get(pkg.labelKey) }}
          </h3>

          <p class="mt-2 text-[0.925rem] text-[rgba(51,51,51,0.68)] leading-[1.5] roboto-font">
            {{ get(pkg.subtitleKey) }}
          </p>

          <ul class="mt-6 space-y-2 list-none p-0 m-0 flex-1">
            <li
              v-for="(feature, index) in getArray(pkg.featuresKey)"
              :key="index"
              class="roboto-font text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[color:var(--gw-ink)]"
            >
              — {{ feature }}
            </li>
          </ul>

          <div class="mt-8 pt-6 border-t gw-rule">
            <div class="flex items-end gap-1">
              <GwNumeralGrad :value="formatPrice(displayPrice(pkg))" />
              <span class="text-xl font-medium text-[color:var(--gw-ink)]">€</span>
            </div>

            <p class="mt-2 gw-mono text-[10.5px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.55)]">
              {{ get('home.pricing_section.per_month') }}
              <span v-if="isYearly"> · {{ get('home.pricing_section.billed_yearly') }}</span>
            </p>

            <p class="mt-1 text-xs text-[rgba(51,51,51,0.5)]">
              {{ get('home.pricing_section.excl_tax') }}
            </p>

            <!-- Tax tooltip -->
            <div
              class="relative inline-block mt-3"
              @mouseenter="openTaxId = pkg.id"
              @mouseleave="openTaxId = null"
            >
              <button
                type="button"
                class="gw-mono text-[10px] uppercase tracking-[0.18em] font-semibold text-[rgba(51,51,51,0.55)] underline decoration-dotted underline-offset-4 hover:text-[color:var(--gw-ink)] transition-colors"
                :aria-expanded="openTaxId === pkg.id"
                @click="toggleTax(pkg.id)"
              >
                + {{ get('pricing.tax') }}
              </button>

              <div
                v-show="openTaxId === pkg.id"
                class="absolute left-0 z-20 mt-2 w-56 rounded-xl border gw-rule-strong bg-white p-4 shadow-[0_12px_40px_-12px_rgba(51,51,51,0.25)]"
                role="tooltip"
              >
                <p class="gw-mono text-[10px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.5)]">
                  {{ get('home.pricing_section.incl_tax') }} ({{ get('home.pricing_section.tax_rate') }}% {{ get('pricing.vat') }})
                </p>
                <p class="mt-2 text-sm font-semibold text-[color:var(--gw-ink)]">
                  €{{ formatPrice(taxDetails(pkg).monthly) }} {{ get('home.pricing_section.per_month') }}
                </p>
                <p v-if="isYearly" class="mt-1 text-sm text-[rgba(51,51,51,0.7)]">
                  €{{ formatPrice(taxDetails(pkg).yearly) }} {{ get('home.pricing_section.per_year') }}
                </p>
              </div>
            </div>
          </div>
        </li>
      </ul>

      <!-- CTAs -->
      <div class="mt-10 flex flex-col sm:flex-row sm:justify-center gap-3">
        <a href="https://admin.growiyo.com" class="gw-btn-grad inline-flex justify-center">
          {{ get('home.pricing_section.start_now') }}
          <GwIcon name="arrow-right" class="w-4 h-4" />
        </a>
        <a :href="contactUrl" class="gw-btn-ghost inline-flex justify-center">
          {{ get('home.pricing_section.contact_us') }}
          <GwIcon name="arrow-right-long" class="w-4 h-4" />
        </a>
      </div>
      <p class="mt-4 text-center gw-mono text-[10.5px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.5)]">
        {{ get('static.pricing.hero.cta_note') }}
      </p>
    </div>
  </section>

  <!-- ===================================================================
    02 · WHAT'S INCLUDED
  ==================================================================== -->
  <AllFeatures />

  <!-- ===================================================================
    04 · COMPARISON
  ==================================================================== -->
  <section class="bg-white border-t gw-rule">
    <div class="max-w-5xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <p class="gw-mono text-[10.5px] font-medium uppercase tracking-[0.22em] text-[rgba(51,51,51,0.5)] mb-3">
        {{ get('static.pricing.comparison.eyebrow') }}
      </p>
      <h2 class="gw-title max-w-2xl">
        {{ get('static.pricing.comparison.heading_prefix') }} <em>{{ get('static.pricing.comparison.heading_emphasis') }}</em>{{ get('static.pricing.comparison.heading_suffix') }}
      </h2>

      <div class="mt-12 border-t gw-rule-strong">
        <!-- Header row -->
        <div class="hidden md:grid grid-cols-12 gap-6 py-3 border-b gw-rule">
          <div class="col-span-5"><span class="gw-mono text-[10px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.45)]">{{ get('static.pricing.comparison.table.platform') }}</span></div>
          <div class="col-span-4"><span class="gw-mono text-[10px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.45)]">{{ get('static.pricing.comparison.table.monthly_price') }}</span></div>
          <div class="col-span-3 text-right"><span class="gw-mono text-[10px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.45)]">{{ get('static.pricing.comparison.table.commission') }}</span></div>
        </div>

        <!-- Growiyo Landing row -->
        <div class="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-6 py-6 border-b gw-rule bg-[var(--gw-paper)] -mx-6 px-6 lg:-mx-10 lg:px-10">
          <div class="md:col-span-5">
            <p class="text-[1rem] font-semibold text-[color:var(--gw-ink)] tracking-[-0.012em]">Growiyo Landing page</p>
            <p class="text-xs text-[rgba(51,51,51,0.55)] mt-1">{{ get('static.pricing.comparison.rows.growiyo_start.note') }}</p>
          </div>
          <div class="md:col-span-4">
            <p class="gw-mono text-[1.5rem] font-semibold text-[color:var(--gw-ink)] tracking-[-0.02em] leading-none">€7,99</p>
            <p class="gw-mono text-[10.5px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.5)] mt-1">{{ get('static.pricing.hero.per_month') }}</p>
          </div>
          <div class="md:col-span-3 md:text-right">
            <p class="gw-mono text-[1.5rem] font-semibold text-[color:var(--gw-ink)] tracking-[-0.02em] leading-none">0&nbsp;%</p>
            <p class="gw-mono text-[10.5px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.5)] mt-1">{{ get('static.pricing.comparison.rows.growiyo_pro.commission_note') }}</p>
          </div>
        </div>

        <!-- Growiyo Ecommerce Basic row -->
        <div class="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-6 py-6 border-b gw-rule bg-[var(--gw-paper)] -mx-6 px-6 lg:-mx-10 lg:px-10">
          <div class="md:col-span-5">
            <p class="text-[1rem] font-semibold text-[color:var(--gw-ink)] tracking-[-0.012em]">Growiyo Ecommerce Basic</p>
            <p class="text-xs text-[rgba(51,51,51,0.55)] mt-1">{{ get('static.pricing.comparison.rows.growiyo_start.note') }}</p>
          </div>
          <div class="md:col-span-4">
            <p class="gw-mono text-[1.5rem] font-semibold text-[color:var(--gw-ink)] tracking-[-0.02em] leading-none">€10</p>
            <p class="gw-mono text-[10.5px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.5)] mt-1">{{ get('static.pricing.hero.per_month') }}</p>
          </div>
          <div class="md:col-span-3 md:text-right">
            <p class="gw-mono text-[1.5rem] font-semibold text-[color:var(--gw-ink)] tracking-[-0.02em] leading-none">2&nbsp;%</p>
            <p class="gw-mono text-[10.5px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.5)] mt-1">{{ get('static.pricing.comparison.rows.growiyo_start.commission_note') }}</p>
          </div>
        </div>

        <!-- Growiyo Ecommerce Advanced row -->
        <div class="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-6 py-6 border-b gw-rule bg-[var(--gw-paper)] -mx-6 px-6 lg:-mx-10 lg:px-10">
          <div class="md:col-span-5">
            <p class="text-[1rem] font-semibold text-[color:var(--gw-ink)] tracking-[-0.012em]">Growiyo Ecommerce Advanced</p>
            <p class="text-xs text-[rgba(51,51,51,0.55)] mt-1">{{ get('static.pricing.comparison.rows.growiyo_pro.note') }}</p>
          </div>
          <div class="md:col-span-4">
            <p class="gw-mono text-[1.5rem] font-semibold text-[color:var(--gw-ink)] tracking-[-0.02em] leading-none">€20</p>
            <p class="gw-mono text-[10.5px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.5)] mt-1">{{ get('static.pricing.hero.per_month') }}</p>
          </div>
          <div class="md:col-span-3 md:text-right">
            <p class="gw-mono text-[1.5rem] font-semibold text-[color:var(--gw-ink)] tracking-[-0.02em] leading-none">1&nbsp;%</p>
            <p class="gw-mono text-[10.5px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.5)] mt-1">{{ get('static.pricing.comparison.rows.growiyo_start.commission_note') }}</p>
          </div>
        </div>

        <!-- Shopify row -->
        <div class="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-6 py-6 border-b gw-rule">
          <div class="md:col-span-5">
            <p class="text-[1rem] text-[color:var(--gw-ink)] tracking-[-0.012em]">{{ get('static.pricing.comparison.rows.shopify.name') }}</p>
            <p class="text-xs text-[rgba(51,51,51,0.55)] mt-1">{{ get('static.pricing.comparison.rows.shopify.note') }}</p>
          </div>
          <div class="md:col-span-4">
            <p class="gw-mono text-[1.25rem] text-[color:var(--gw-ink)] tracking-[-0.015em] leading-none">$29 – $299</p>
            <p class="gw-mono text-[10.5px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.5)] mt-1">{{ get('static.pricing.hero.per_month') }}</p>
          </div>
          <div class="md:col-span-3 md:text-right">
            <p class="gw-mono text-[1.25rem] text-[color:var(--gw-ink)] tracking-[-0.015em] leading-none">2,2 – 2,9 %</p>
            <p class="gw-mono text-[10.5px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.5)] mt-1">{{ get('static.pricing.comparison.rows.shopify.commission_note') }}</p>
          </div>
        </div>

        <!-- WooCommerce row -->
        <div class="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-6 py-6 border-b gw-rule">
          <div class="md:col-span-5">
            <p class="text-[1rem] text-[color:var(--gw-ink)] tracking-[-0.012em]">{{ get('static.pricing.comparison.rows.woocommerce.name') }}</p>
            <p class="text-xs text-[rgba(51,51,51,0.55)] mt-1">{{ get('static.pricing.comparison.rows.woocommerce.note') }}</p>
          </div>
          <div class="md:col-span-4">
            <p class="gw-mono text-[1.25rem] text-[color:var(--gw-ink)] tracking-[-0.015em] leading-none">DIY</p>
            <p class="gw-mono text-[10.5px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.5)] mt-1">{{ get('static.pricing.comparison.rows.woocommerce.price_note') }}</p>
          </div>
          <div class="md:col-span-3 md:text-right">
            <p class="gw-mono text-[1.25rem] text-[color:var(--gw-ink)] tracking-[-0.015em] leading-none">var.</p>
            <p class="gw-mono text-[10.5px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.5)] mt-1">{{ get('static.pricing.comparison.rows.woocommerce.commission_note') }}</p>
          </div>
        </div>
      </div>

      <p class="mt-6 text-xs text-[rgba(51,51,51,0.5)] max-w-xl">
        {{ get('static.pricing.comparison.disclaimer_1') }}
      </p>
      <p class="mt-6 text-xs text-[rgba(51,51,51,0.5)] max-w-xl">
        {{ get('static.pricing.comparison.disclaimer_2') }}
      </p>
    </div>
  </section>

  <!-- ===================================================================
    05 · FAQ
  ==================================================================== -->
  <section class="bg-white border-t gw-rule">
    <div class="max-w-3xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <p class="gw-mono text-[10.5px] font-medium uppercase tracking-[0.22em] text-[rgba(51,51,51,0.5)] mb-3 text-center">
        {{ get('static.pricing.faq.eyebrow') }}
      </p>
      <h2 class="gw-title text-center">
        <em>{{ get('static.pricing.faq.heading_emphasis') }}</em> {{ get('static.pricing.faq.heading_rest') }}
      </h2>

      <div class="mt-12 border-t gw-rule-strong">
        <div v-for="(item, idx) in faqs" :key="idx" class="border-b gw-rule">
          <button
            type="button"
            class="w-full flex items-center justify-between gap-6 py-6 text-left group"
            :aria-expanded="openFaq === idx"
            @click="openFaq = openFaq === idx ? null : idx"
          >
            <span class="text-[1rem] lg:text-[1.05rem] font-medium text-[color:var(--gw-ink)] tracking-[-0.012em] leading-snug">
              {{ get(item.q) }}
            </span>
            <span
              class="w-8 h-8 flex items-center justify-center shrink-0 transition-all duration-300"
              :class="openFaq === idx ? 'text-[color:var(--gw-ink)]' : 'text-[rgba(51,51,51,0.4)] group-hover:text-[color:var(--gw-ink)]'"
            >
              <GwIcon name="plus" class="w-4 h-4 transition-transform duration-400" :class="openFaq === idx ? 'rotate-45' : ''" />
            </span>
          </button>
          <div
            class="overflow-hidden transition-all duration-400"
            :style="{ maxHeight: openFaq === idx ? '600px' : '0px', opacity: openFaq === idx ? 1 : 0 }"
          >
            <p class="pb-6 pr-12 text-[0.95rem] text-[rgba(51,51,51,0.7)] leading-[1.65]">{{ get(item.a) }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ===================================================================
    07 · QUOTE
  ==================================================================== -->
  <section class="bg-white border-t gw-rule">
    <div class="max-w-3xl mx-auto px-6 lg:px-10 py-20 lg:py-28 text-center">
      <div aria-hidden="true" class="mx-auto mb-8 w-10 h-px" style="background: var(--gw-gradient);"></div>
      <blockquote>
        <p class="text-[1.5rem] lg:text-[1.85rem] font-light leading-[1.4] tracking-[-0.02em] text-[color:var(--gw-ink)]" style="font-family: 'Plus Jakarta Sans', sans-serif;">
          {{ get('static.pricing.quote.prefix') }}
          <em style="background: var(--gw-gradient); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; font-style: italic; font-weight: 800;">{{ get('static.pricing.quote.emphasis') }}</em>{{ get('static.pricing.quote.suffix') }}
        </p>
      </blockquote>
      <div aria-hidden="true" class="mx-auto mt-8 w-10 h-px" style="background: var(--gw-gradient);"></div>
    </div>
  </section>

  <!-- ===================================================================
    08 · CLOSING
  ==================================================================== -->
  <section class="gw-paper border-t gw-rule">
    <div class="max-w-3xl mx-auto px-6 lg:px-10 py-20 lg:py-28 text-center">
      <p class="gw-title max-w-xl mx-auto" style="font-size: clamp(1.5rem, 3vw, 2rem);">
        {{ get('static.pricing.closing.prefix') }} <em>{{ get('static.pricing.closing.emphasis') }}</em>{{ get('static.pricing.closing.suffix') }}
      </p>
      <div class="mt-10 flex flex-col sm:flex-row sm:justify-center gap-3">
        <a href="https://admin.growiyo.com" class="gw-btn-grad inline-flex justify-center">
          {{ get('home.pricing_section.start_now') }}
          <GwIcon name="arrow-right" class="w-4 h-4" />
        </a>
        <a :href="contactUrl" class="gw-btn-ghost inline-flex justify-center">
          {{ get('home.pricing_section.contact_us') }}
          <GwIcon name="arrow-right-long" class="w-4 h-4" />
        </a>
      </div>
      <p class="mt-4 gw-mono text-[10.5px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.5)]">
        {{ get('static.pricing.closing.note') }}
      </p>
    </div>
  </section>
</template>
